/**
 * Кнопка «назад» на Андроиде: сначала закрывается то, что открыто сверху
 * (нижнее окно, меню, наложение звонка), потом — шаг назад по экранам
 * (переписка → список, сделка → откуда пришли), а на корневой вкладке —
 * «Нажмите ещё раз, чтобы выйти» и выход по второму нажатию за 2 секунды.
 *
 * Слои регистрируются в маленьком стеке: окно при показе кладёт сюда свою
 * «закрывашку» и снимает её при закрытии. Верхний слой — последний открытый.
 */
import { useEffect, useRef } from 'react';

type Closer = () => void;

interface Layer {
  id: number;
  close: () => void;
}

let seq = 0;
const stack: Layer[] = [];

/** Положить слой наверх. Возвращает «снять» (вызывать при закрытии/размонтировании). */
export function pushLayer(close: Closer): () => void {
  const layer: Layer = { id: ++seq, close };
  stack.push(layer);
  return () => {
    const i = stack.findIndex((l) => l.id === layer.id);
    if (i >= 0) stack.splice(i, 1);
  };
}

/** Закрыть верхний слой. false — открытых слоёв нет. */
export function closeTopLayer(): boolean {
  const top = stack[stack.length - 1];
  if (!top) return false;
  top.close();
  return true;
}

export function layerCount(): number {
  return stack.length;
}

/** Только для проверок. */
export function _resetLayers(): void {
  stack.length = 0;
}

/**
 * Окно/меню регистрирует себя, пока смонтировано (и пока active).
 * Всегда зовётся самая свежая onClose — без перерегистрации на каждый рендер,
 * иначе окно «прыгало» бы наверх стека и порядок слоёв ломался.
 */
export function useBackLayer(onClose: Closer, active = true): void {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    if (!active) return;
    return pushLayer(() => ref.current());
  }, [active]);
}

// ── Решение по нажатию — чистая функция, проверяется тестами ──

/** Корневые вкладки нижнего меню. */
export const ROOT_TABS = ['/inbox', '/pipeline', '/calls', '/calendar', '/more'] as const;

/** Окно для второго нажатия «выйти». */
export const EXIT_WINDOW_MS = 2000;

export type BackAction =
  | { kind: 'close-layer' }
  | { kind: 'history-back' }
  | { kind: 'navigate'; to: string }
  | { kind: 'arm-exit' }
  | { kind: 'exit' };

export interface BackInput {
  /** Есть открытые слои (зарегистрированные или запасной DOM-поиск). */
  hasLayer: boolean;
  pathname: string;
  /** В истории приложения есть куда вернуться (location.key !== 'default'). */
  canGoBack: boolean;
  /** Когда в прошлый раз показали «нажмите ещё раз» (0 — не показывали). */
  lastExitPromptAt: number;
  now: number;
}

/** Корневая вкладка для пути: '/inbox/5/12' → '/inbox'; null — путь вне вкладок. */
export function rootTabOf(pathname: string): string | null {
  const first = '/' + (pathname.split('/').filter(Boolean)[0] ?? '');
  return (ROOT_TABS as readonly string[]).includes(first) ? first : null;
}

export function decideBack(i: BackInput): BackAction {
  if (i.hasLayer) return { kind: 'close-layer' };
  const tab = rootTabOf(i.pathname);
  const path = i.pathname.replace(/\/+$/, '') || '/';
  const isRoot = tab !== null && path === tab;
  if (!isRoot) {
    // Глубже вкладки (переписка, сделка): шаг назад по истории, а если в неё
    // пришли напрямую (уведомление, ссылка) — к списку своей вкладки.
    if (i.canGoBack) return { kind: 'history-back' };
    return { kind: 'navigate', to: tab ?? '/inbox' };
  }
  if (i.lastExitPromptAt > 0 && i.now - i.lastExitPromptAt <= EXIT_WINDOW_MS) return { kind: 'exit' };
  return { kind: 'arm-exit' };
}
